import { EmployeesAndAppraisalsFragment, EmployeeLevelEnum, AppraisalSeasonDto, SkillEmployeeStatusEnum } from '../../../types'

export interface OtherAppraisalFragment extends Omit<EmployeesAndAppraisalsFragment, 'id' | 'otherAppraisals'> {
  isChild: boolean
  key: string
}
export interface ExtendedEmployeesAndAppraisalsFragment extends EmployeesAndAppraisalsFragment {
  otherAppraisals: OtherAppraisalFragment[]
  isChild: boolean
  key: string
}

export const APPRAISAL_NO_OFFICE_FILTER_VALUE = 'NO_OFFICE'

export const appraisalTabInitialFilter: AppraisalTabEmployeeFilter = {
  skillStatus: undefined,
  todo: undefined,
  mandatory: undefined,
  employeeLevel: undefined,
  bu: undefined,
  office: undefined,
  appraisalSeasonYears: [],
}

export const transformAppraisalData = (tableData: EmployeesAndAppraisalsFragment[]): ExtendedEmployeesAndAppraisalsFragment[] => {
  if (tableData.length) {
    return tableData.reduce((acc: ExtendedEmployeesAndAppraisalsFragment[], current: EmployeesAndAppraisalsFragment, index: number) => {
      acc.push({ ...current, otherAppraisals: [], isChild: false, key: `${current.id}-parent-${index}` })

      return acc
    }, [])
  }

  return []
}

export interface AppraisalTabEmployeeFilter {
  skillStatus: SkillEmployeeStatusEnum | undefined
  todo: boolean | undefined
  mandatory: boolean | undefined
  employeeLevel: EmployeeLevelEnum | undefined
  bu: string | undefined
  office: string | undefined
  appraisalSeasonYears: string[]
}

export const filterEmployeeOfAppraisalTab = (
  employeeFilter: AppraisalTabEmployeeFilter,
  employees: ExtendedEmployeesAndAppraisalsFragment[],
): ExtendedEmployeesAndAppraisalsFragment[] => {
  let result = employees
  if (!!employeeFilter.skillStatus)
    result = result.filter((e) => {
      const employeeSkillStatus: SkillEmployeeStatusEnum = !!e.skillStatus ? e.skillStatus.skillStatus : SkillEmployeeStatusEnum.Unknown
      if (
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.CvIncomplete ||
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.NearlyExpiring ||
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.PendingAiReview ||
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.RequiresImprovement ||
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.UnknownYellow
      ) {
        return (
          employeeSkillStatus === SkillEmployeeStatusEnum.CvIncomplete ||
          employeeSkillStatus === SkillEmployeeStatusEnum.NearlyExpiring ||
          employeeSkillStatus === SkillEmployeeStatusEnum.PendingAiReview ||
          employeeSkillStatus === SkillEmployeeStatusEnum.RequiresImprovement ||
          employeeSkillStatus === SkillEmployeeStatusEnum.UnknownYellow
        )
      }
      return employeeSkillStatus === employeeFilter.skillStatus
    })
  if (employeeFilter.todo !== undefined) result = result.filter((e) => !!e.meetingToDoInCurrentAppraisalSeason === employeeFilter.todo)
  if (!!employeeFilter.employeeLevel) result = result.filter((e) => e.employee.currentLevel === employeeFilter.employeeLevel)
  if (!!employeeFilter.bu) result = result.filter((e) => e.employee.buName === employeeFilter.bu)
  if (!!employeeFilter.office) {
    result =
      employeeFilter.office === APPRAISAL_NO_OFFICE_FILTER_VALUE
        ? result.filter((e) => !e.branchOfficeCode)
        : result.filter((e) => e.branchOfficeCode === employeeFilter.office)
  }
  if (employeeFilter.mandatory !== undefined) result = result.filter((e) => !!e.mandatory === employeeFilter.mandatory)
  if (employeeFilter.appraisalSeasonYears.length) {
    const noMeetingsIncluded = employeeFilter.appraisalSeasonYears.includes('No Meetings')
    result = result.filter((e) => {
      const employeeYear: string | undefined = e.appraisal ? `${e.appraisal.appraisalSeason.year}` : undefined
      return !!employeeYear ? employeeFilter.appraisalSeasonYears.includes(employeeYear) : noMeetingsIncluded
    })
  }

  return result
}

export const areAppraisalTabEmployeeFiltersEquals = (obj1: AppraisalTabEmployeeFilter, obj2: AppraisalTabEmployeeFilter): boolean => {
  if (obj1.skillStatus !== obj2.skillStatus) return false
  if (obj1.todo !== obj2.todo) return false
  if (obj1.mandatory !== obj2.mandatory) return false
  if (obj1.employeeLevel !== obj2.employeeLevel) return false
  if (obj1.bu !== obj2.bu) return false
  if (obj1.office !== obj2.office) return false
  if (obj1.appraisalSeasonYears.length !== obj2.appraisalSeasonYears.length) return false
  for (let i = 0; i < obj1.appraisalSeasonYears.length; i++) {
    if (obj1.appraisalSeasonYears[i] !== obj2.appraisalSeasonYears[i]) {
      return false
    }
  }
  return true
}

export const possibleAppraisalSeasons = (employees: EmployeesAndAppraisalsFragment[]): string[] => {
  const result: string[] = []
  for (const e of employees) {
    const appraisalSeason: AppraisalSeasonDto | undefined = e.appraisal ? e.appraisal.appraisalSeason : undefined
    if (appraisalSeason) result.push(`${appraisalSeason.year}`)
  }
  result.push('No Meetings')
  return [...new Set(result)]
}

export const possibleOffices = (employees: EmployeesAndAppraisalsFragment[]): string[] => {
  const codes = [...new Set(employees.map((e) => e.branchOfficeCode).filter((code): code is string => !!code))].sort((officeA, officeB) =>
    officeA.localeCompare(officeB),
  )
  if (employees.some((e) => !e.branchOfficeCode)) {
    codes.push(APPRAISAL_NO_OFFICE_FILTER_VALUE)
  }
  return codes
}
